import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { auth } from "../../firebase";
import {
  getNetworkByInviteCode,
  joinImpactNetwork,
} from "../../services/impactNetworks";
import "./ImpactNetworks.css";


export default function JoinImpactNetwork() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const user = auth.currentUser;
  
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadNetwork() {
      try {
        const found = await getNetworkByInviteCode(inviteCode);
        setNetwork(found);
      } catch (err) {
        console.error(err);
        setError("Unable to load Impact Network.");
      } finally {
        setLoading(false);
      }
    }

    loadNetwork();
  }, [inviteCode]);

  const handleJoin = async () => {
    setJoining(true);
    setError("");

    try {
      if (!user) {
        navigate(`/login?redirect=/join-network/${inviteCode}`);
        return;
      }

      const result = await joinImpactNetwork({ user, inviteCode });
      navigate(`/donor/impact-networks/${result.networkId}`);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to join Impact Network.");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="impact-network-page">
        Loading Impact Network...
      </div>
    );
  }

  if (!network) {
    return (
      <div className="impact-network-page">
        <section className="network-panel">
          <h1>Impact Network Not Found</h1>
          <p>This invite link may be invalid or expired.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="impact-network-page">
      <nav className="network-top-nav">
        <Link to="/" className="network-brand-link">
          somebody.gives
        </Link>

        <div className="network-top-nav-links">
          <Link to="/give/browse">Browse Needs</Link>
          {user && <Link to="/donor/impact-networks">My Networks</Link>}
        </div>
      </nav>

      <section className="network-hero">
        <div>
          <div className="network-eyebrow">Invitation</div>
          <h1>Join {network.name}</h1>

          <p className="network-hero-text">
            {network.description ||
              "Collective impact through recurring nonprofit needs."}
          </p>

          <p className="network-hero-subtext">
            Members continue supporting needs personally, while tracking
            collective impact together.
          </p>
        </div>
      </section>

      <section className="network-panel">
        <div className="network-eyebrow">Network Details</div>
        <h2>{network.name}</h2>

        {network.focusAreas?.length > 0 && (
          <>
            <p style={{ marginBottom: "12px" }}>
              Preferred areas of community focus:
            </p>

            <div className="network-tag-row">
              {network.focusAreas.map((area) => (
                <span className="network-tag" key={area}>
                  {area}
                </span>
              ))}
            </div>
          </>
        )}

        {error && (
          <div className="network-error-box" style={{ marginTop: "18px" }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: "24px" }}>
          <button
            className="network-primary-button"
            onClick={handleJoin}
            disabled={joining}
          >
            {joining ? "Joining..." : "Join Impact Network"}
          </button>
        </div>
      </section>

      <section className="network-panel">
        <div className="network-eyebrow">How It Works</div>
        <h2>Collective Impact, Individual Giving</h2>

        <div className="network-simple-list">
          <div>
            <strong>Join the network</strong>
            <p>Become part of the group's collective impact story.</p>
          </div>

          <div>
            <strong>Adopt needs you care about</strong>
            <p>
              Your commitments remain personal and under your control.
            </p>
          </div>

          <div>
            <strong>Attribute commitments to the network</strong>
            <p>
              Help show the group's combined impact across local nonprofits.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}